/**
 * The little bit of formatting a note is allowed, and the sanitiser that
 * keeps it little.
 *
 * A note is not a document. What people actually want is to make one word
 * bold, put three things in a list, highlight the bit that matters and colour
 * a warning red — so that is the whole vocabulary, and anything else that
 * arrives is thrown away rather than escaped into the page.
 *
 * **Aaron, Oct 25 (note 1.4)**: a task's notes are rich text — bold, italic,
 * underline, strikethrough, a highlight, a colour, a size, bulleted and
 * numbered lists, an indent and a link. The vocabulary grew by exactly those,
 * each as something this file can check: a size is one of four named steps
 * (`NOTE_SIZES`), an indent one of four levels on a paragraph (a list indents
 * by nesting), and a link's address must be `http`, `https` or `mailto` and
 * leaves with `rel="noopener noreferrer nofollow"` and a new tab.
 *
 * Sanitising here rather than in the component, and on the server rather than
 * only in the browser, because a note is stored HTML: it is written by one
 * person and rendered to everybody else, which is the shape of every
 * stored-XSS bug there has ever been. The rule is an allow-list — tags not on
 * it lose their markup and keep their text, attributes not on it are dropped,
 * and a colour has to be one of ours.
 */
/** Tags a note may contain. Everything else is unwrapped to its text. */
const ALLOWED = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'ul', 'ol', 'li', 'br', 'p', 'span', 'a']);
/** Tags whose *contents* go too — a script's text is not text. */
const DROPPED = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template']);
/**
 * The colours a word in a note may be.
 *
 * Token names, resolved to CSS by the renderer, never hex. A note is stored
 * data that would otherwise carry a colour straight past the palette — and a
 * note written in a colour that does not exist in dark mode is a note nobody
 * can read at night.
 */
export const NOTE_COLORS = [
    { value: 'text', label: 'Default' },
    { value: 'danger', label: 'Red' },
    { value: 'warn', label: 'Amber' },
    { value: 'accent', label: 'Green' },
    { value: 'info', label: 'Blue' },
];
const COLOR_NAMES = new Set(NOTE_COLORS.map((entry) => entry.value));
/**
 * The sizes a word in a note may be (note 1.4), as steps rather than points:
 * a note is read on a 480 sheet, a phone and a record's timeline, and *Large*
 * means the same thing on all three where *18px* would not. *Normal* is no
 * class at all, so every note written before sizes existed is Normal.
 */
export const NOTE_SIZES = [
    { value: 'sm', label: 'Small' },
    { value: 'normal', label: 'Normal' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Huge' },
];
const SIZE_NAMES = new Set(NOTE_SIZES.filter((entry) => entry.value !== 'normal').map((entry) => entry.value));
/** How far a paragraph may be indented (note 1.4). A list item indents by nesting instead. */
export const NOTE_INDENT_MAX = 4;
/** The one class a span may carry for a colour, and the one for a highlight. */
export const noteColorClass = (color) => `rt-${color}`;
export const NOTE_MARK_CLASS = 'rt-mark';
/** The class a span carries for a size — `rt-size-lg`. */
export const noteSizeClass = (size) => `rt-size-${size}`;
/** The class a paragraph carries for an indent — `rt-indent-2`. */
export const noteIndentClass = (level) => `rt-indent-${level}`;
/**
 * A person named in a note — *@Maya Patel* (the record composer's *@ to
 * mention someone*, Aaron's answer "yes" to the rich composer, Oct 25). A
 * span with this class and the person's id in `data-user`, and nothing else:
 * the id is checked to be a uuid, and a mention without one is plain words.
 */
export const NOTE_MENTION_CLASS = 'rt-mention';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const spanClassAllowed = (value) => value === NOTE_MARK_CLASS || value === NOTE_MENTION_CLASS
    || (value.startsWith('rt-size-') && SIZE_NAMES.has(value.slice(8)))
    || (value.startsWith('rt-') && COLOR_NAMES.has(value.slice(3)));
const blockClassAllowed = (value) => {
    const match = /^rt-indent-(\d)$/.exec(value);
    return Boolean(match) && Number(match[1]) >= 1 && Number(match[1]) <= NOTE_INDENT_MAX;
};
/** What a link carries out, whatever it came in with. */
const LINK_REL = 'noopener noreferrer nofollow';
/**
 * A note's HTML, with everything not on the allow-list removed.
 *
 * Deliberately a small hand-written parser rather than a dependency: the
 * grammar is eight tags and one attribute, and a sanitiser you cannot read in
 * one sitting is a sanitiser nobody audits. It never emits an attribute it
 * did not verify, and it never emits a tag it did not recognise.
 */
export function sanitizeNote(html) {
    let out = '';
    let i = 0;
    const open = [];
    while (i < html.length) {
        const lt = html.indexOf('<', i);
        if (lt === -1) {
            out += escapeText(html.slice(i));
            break;
        }
        out += escapeText(html.slice(i, lt));
        /*
         * A `<` is only the start of a tag when a tag could follow it. "5 < 6 & 7
         * > 2" is arithmetic, and reading it as a tag swallowed the words between
         * the two signs — which is a sanitiser eating a sentence.
         */
        if (!/[a-zA-Z/!]/.test(html[lt + 1] ?? '')) {
            out += '&lt;';
            i = lt + 1;
            continue;
        }
        const gt = html.indexOf('>', lt);
        if (gt === -1) {
            out += escapeText(html.slice(lt));
            break;
        }
        const raw = html.slice(lt + 1, gt).trim();
        const closing = raw.startsWith('/');
        const name = (closing ? raw.slice(1) : raw).split(/[\s/>]/)[0].toLowerCase();
        if (DROPPED.has(name)) {
            // Skip to the matching close, contents and all.
            const end = html.toLowerCase().indexOf(`</${name}`, gt);
            i = end === -1 ? html.length : (html.indexOf('>', end) + 1 || html.length);
            continue;
        }
        if (!ALLOWED.has(name)) {
            i = gt + 1;
            continue;
        }
        if (closing) {
            /*
             * The innermost open of this name, kept or skipped: a skipped span
             * inside a kept one closes itself, not the highlight around it.
             */
            const at = Math.max(open.lastIndexOf(name), open.lastIndexOf(`${name}-skipped`));
            // A close with no open is somebody's broken paste, not markup.
            if (at !== -1) {
                if (!open[at].endsWith('-skipped'))
                    out += `</${name}>`;
                open.splice(at, 1);
            }
            i = gt + 1;
            continue;
        }
        if (name === 'br') {
            out += '<br>';
            i = gt + 1;
            continue;
        }
        if (name === 'a') {
            const href = safeHref(raw);
            // A link to nowhere we allow keeps its words and loses its address.
            if (!href) {
                i = gt + 1;
                open.push('a-skipped');
                continue;
            }
            out += `<a href="${href}" rel="${LINK_REL}" target="_blank">`;
            open.push('a');
            i = gt + 1;
            continue;
        }
        let cls = name === 'span' ? keptClasses(raw, spanClassAllowed)
            : name === 'p' ? keptClasses(raw, blockClassAllowed) : '';
        /* A mention is its class and a person's id, nothing else; without a real id it is plain words. */
        if (name === 'span' && cls.split(' ').includes(NOTE_MENTION_CLASS)) {
            const user = mentionUser(raw);
            if (user) {
                out += `<span class="${NOTE_MENTION_CLASS}" data-user="${user}">`;
                open.push('span');
                i = gt + 1;
                continue;
            }
            cls = cls.split(' ').filter((value) => value !== NOTE_MENTION_CLASS).join(' ');
        }
        // A span with nothing we kept is not worth a tag; its text stands alone.
        if (name === 'span' && !cls) {
            i = gt + 1;
            open.push('span-skipped');
            continue;
        }
        out += cls ? `<${name} class="${cls}">` : `<${name}>`;
        open.push(name);
        i = gt + 1;
    }
    // Close anything left hanging, innermost first.
    for (const name of [...open].reverse()) {
        if (!name.endsWith('-skipped'))
            out += `</${name}>`;
    }
    return out;
}
/** A mention's person: `data-user`, when it is a uuid. */
function mentionUser(raw) {
    const match = /\bdata-user\s*=\s*("([^"]*)"|'([^']*)')/i.exec(raw);
    const value = (match?.[2] ?? match?.[3] ?? '').trim();
    return UUID.test(value) ? value.toLowerCase() : null;
}
/**
 * The people a note mentions, each once, in the order they first appear —
 * read from the sanitised note, so only a mention the note will keep counts.
 */
export function noteMentions(html) {
    const out = [];
    for (const match of sanitizeNote(html).matchAll(/<span class="rt-mention" data-user="([0-9a-f-]{36})">/g)) {
        if (!out.includes(match[1]))
            out.push(match[1]);
    }
    return out;
}
/** The `class` values a tag may keep: a span its colour, size and highlight; a paragraph its indent. */
function keptClasses(raw, allowed) {
    const match = /class\s*=\s*("([^"]*)"|'([^']*)')/i.exec(raw);
    if (!match)
        return '';
    const values = (match[2] ?? match[3] ?? '').split(/\s+/).filter(allowed);
    return [...new Set(values)].join(' ');
}
/**
 * A link's address, if it is one a note may carry: `http`, `https` or
 * `mailto`, nothing that could be read as anything else, escaped for the
 * attribute. The entities the editor writes are read first, so a link saved
 * twice is the same link — and so `&#106;avascript:` is not an address
 * either, since it does not start with one of the three.
 */
function safeHref(raw) {
    const match = /\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))/i.exec(raw);
    if (!match)
        return null;
    const href = (match[2] ?? match[3] ?? match[4] ?? '')
        .replace(/&amp;/gi, '&').replace(/&quot;/gi, '"').replace(/&#39;/g, "'")
        .replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').trim();
    if (!/^(https?:\/\/|mailto:)/i.test(href))
        return null;
    if (/[\s\u0000-\u001f"'<>`\\]/.test(href) || href.length > 2000)
        return null;
    return href.replace(/&/g, '&amp;');
}
/*
 * `&` is escaped unless it already starts an entity, so sanitising a note that
 * has been sanitised before leaves it alone. Without that, every save turned
 * `&lt;` into `&amp;lt;` and a note quietly grew a layer of escaping each
 * time somebody edited it.
 */
const ENTITY = /^&(#\d+|#x[0-9a-f]+|[a-z]+);/i;
const escapeText = (text) => {
    let out = '';
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (ch === '<') {
            out += '&lt;';
            continue;
        }
        if (ch === '>') {
            out += '&gt;';
            continue;
        }
        if (ch === '&') {
            const rest = text.slice(i);
            const entity = ENTITY.exec(rest);
            if (entity) {
                out += entity[0];
                i += entity[0].length - 1;
                continue;
            }
            out += '&amp;';
            continue;
        }
        out += ch;
    }
    return out;
};
/**
 * A note as plain text — for a list column, a search index, an SMS.
 *
 * Not every surface renders HTML, and a crew's visit showing `<b>` is worse
 * than a crew's visit showing nothing.
 */
export function noteText(html) {
    return sanitizeNote(html)
        .replace(/<li>/gi, '• ')
        /* A list item holds a paragraph once the rich editor wrote it: one line, not two. */
        .replace(/<\/p>\s*<\/li>/gi, '</li>')
        .replace(/<\/(p|li|ul|ol)>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/** Whether a note has anything in it once the markup is taken off. */
export const noteIsEmpty = (html) => noteText(html).length === 0;
// ---------------------------------------------------------------------------
// Plain notes and rich ones (Aaron, Oct 25, note 1.4)
// ---------------------------------------------------------------------------
/**
 * Whether a stored note is markup at all. A task made from a text or an
 * email, a dictated note and every task typed before notes were rich carries
 * plain words with line breaks — and plain words go on being plain: an
 * ampersand in them is not escaped on the way in, because the surfaces that
 * print them as text (a list's second line, a reminder, an export) would
 * print `&amp;`.
 */
export const isNoteHtml = (text) => /<[a-zA-Z!/]/.test(text);
/** What a writer stores: markup sanitised, plain words as they were typed. */
export const cleanNoteBody = (text) => (isNoteHtml(text) ? sanitizeNote(text) : text);
/**
 * A stored note as the rich editor opens it: markup sanitised, plain words a
 * paragraph a line — so a note written as three lines in March opens as
 * three lines, not one run-on sentence.
 */
export function noteHtmlOf(text) {
    if (isNoteHtml(text))
        return sanitizeNote(text);
    if (text.trim() === '')
        return '';
    return text.split(/\r?\n/).map((line) => `<p>${escapeText(line)}</p>`).join('');
}
//# sourceMappingURL=rich-text.js.map